import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AiFill} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import type {CSSProperties, ReactNode} from 'react';

// AiFill paints with the accent token; pointing it at currentColor makes the
// sparkle match the summary text.
const AI_ICON_STYLE = {'--dig-color__icon__accent': 'currentColor'} as CSSProperties;
// One text line tall, so the sparkle centers on the summary's first line.
// (Truncate flattens children to a string, so the icon can't go inside it.)
const AI_ICON_SLOT_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  height: '1lh',
  flexShrink: 0,
};

/** /workstreams: the sparkle that marks an AI summary, beside its text. */
export const SummarySparkle = ({size}: {size?: 'small'}) => (
  <Text size={size} color="subtle" style={AI_ICON_SLOT_STYLE}>
    <UIIcon src={AiFill} size="small" aria-label="AI summary" style={AI_ICON_STYLE} />
  </Text>
);

// In a card the sparkle takes the byline avatar's place (xsmall Avatar, 18px,
// then the LabelGroup's 8px gap), so the summary lines up with the name above.
// A person's edit keeps the empty slot, so every card's text starts there.
const BYLINE_AVATAR_PX = 18;
const BYLINE_AVATAR_GAP_PX = 8;

// Summaries run a little over one line; "pretty" wrapping evens out the last
// line instead of leaving one or two words alone on it.
const SUMMARY_TEXT_STYLE: CSSProperties = {fontStyle: 'italic', textWrap: 'pretty', minWidth: 0};

const BylineSummaryRow = ({sparkle, children}: {sparkle: boolean; children: ReactNode}) => (
  <Box display="flex" alignItems="flex-start" style={{gap: BYLINE_AVATAR_GAP_PX, minWidth: 0}}>
    <Box display="flex" justifyContent="center" style={{width: BYLINE_AVATAR_PX, flexShrink: 0}}>
      {sparkle && <SummarySparkle size="small" />}
    </Box>
    {children}
  </Box>
);

/**
 * /workstreams: an update's TL;DR as one quiet line (small italic) — a
 * collapsed card's excerpt (clamped to `lines`), and the lead-in under an open
 * card's byline. The sparkle marks an AI-written one; a person's edit has none.
 */
export const SummaryLine = ({
  summary,
  lines,
  edited,
}: {
  summary: string;
  lines?: number;
  edited?: boolean;
}) => (
  <BylineSummaryRow sparkle={!edited}>
    <Text tagName="div" size="small" color="subtle" style={SUMMARY_TEXT_STYLE}>
      {lines ? <Truncate lines={lines}>{summary}</Truncate> : summary}
    </Text>
  </BylineSummaryRow>
);

/**
 * /workstreams: the latest update's TL;DR (italic) at the top of the detail
 * page, above Updates: it works in the north star too, so it summarizes the
 * workstream, not one update. No pull-quote bar. Sparkle as in SummaryLine.
 */
export const UpdateSummaryQuote = ({summary, edited}: {summary: string; edited?: boolean}) => (
  <Box display="flex" alignItems="flex-start" style={{gap: 6}}>
    {!edited && <SummarySparkle />}
    <Text color="subtle" style={SUMMARY_TEXT_STYLE}>
      {summary}
    </Text>
  </Box>
);

/**
 * /workstreams: holds the summary's place while AI writes it. Small (a card's)
 * sits under the byline like SummaryLine; the detail page's like the quote.
 */
export const SummaryPending = ({size}: {size?: 'small'}) =>
  size ? (
    <BylineSummaryRow sparkle>
      <Text tagName="div" size="small" color="faint" style={{fontStyle: 'italic', minWidth: 0}}>
        {t('update_summary_generating')}
      </Text>
    </BylineSummaryRow>
  ) : (
    <Box display="flex" alignItems="flex-start" style={{gap: 6}}>
      <SummarySparkle />
      <Text color="faint" style={{fontStyle: 'italic', minWidth: 0}}>
        {t('update_summary_generating')}
      </Text>
    </Box>
  );
